import React from "react";
import Marquee from "react-fast-marquee";
import { Star, Quote } from "lucide-react";
import Reveal from "@/components/motion/Reveal";
import HexMark from "@/components/motion/HexMark";
import { LOGO_URL } from "@/constants/site";

const TESTIMONIALS = [
  {
    quote:
      "Dion was great to work with! I partnered with Dion for Day & Zimmermann's Engineering, Construction & Maintenance Conference for the senior leadership. Dion was phenomenal through the planning and execution of this endeavor. He provided consistent updates, meaningful inputs and effectively managed the budget using his highly proficient negotiating skills to save money while optimizing the attendees' experience. I'm looking forward to working with him on next year's event!",
    name: "Dr. Tiffani (Braswell) Worthy, PMP",
    role: "Authentic Leader | Elected Official | Executive Coach | Diversity Equity Inclusion Strategist",
    initials: "TW",
    photo: "/testimonials/person-5.jpeg",
  },
  {
    quote:
      "Dion is a very knowledgeable, dedicated, and experienced Project Manager with both domestic and international experience. He does not hesitate to take on new challenges while offering creative solutions for his clients. Dion strives to deliver white glove service to his customers and meeting attendees and is extremely personable, he would be an asset to anyone who is looking for a seasoned meeting professional.",
    name: "Joyce Wooden",
    role: "Sr. Manager, Global Meetings & Events",
    initials: "JW",
    photo: "/testimonials/person-6.jpeg",
  },
  {
    quote:
      "One of the world's most talented event planners and most effective motivational speakers. Sir Dion carries my highest and best recommendation!",
    name: "Professor Joe Goldblatt",
    role: "Author · Event Management Pioneer",
    logo: true,
  },
  {
    quote:
      "Dion brings a great level of enthusiasm when teaching at Temple! His Introduction to Event Management course was thoroughly intriguing and insightful. Best class I've taken thus far.",
    name: "Maya Turner, CGSP",
    role: "Project Manager | Freelance Event Coordinator | Data Analytics",
    initials: "MT",
    photo: "/testimonials/person-1.jpeg",
  },
  {
    quote:
      "It has been my pleasure to get to know and work for Sir Dion Maggee, who has the ability to share clear messages and make complex ideas or tasks easy to understand for people from around the world working for him. I always felt his support, I am very grateful for that!",
    name: "Pita Cruz y Curro",
    role: "Especialista en Marketing, Comunicación y Eventos en Volvo Group",
    initials: "PC",
    photo: "/testimonials/person-8.jpeg",
  },
  {
    quote:
      "Dion was amazing to work with! It was a sincere pleasure. Thank you for the wonderful professionalism you brought to our partnership.",
    name: "Tangan Lee",
    role: "Vice President, Communications Diversity & Inclusion, CBS Studios",
    initials: "TL",
    photo: "/testimonials/person-2.jpeg",
  },
  {
    quote:
      "Dion gets it! He clearly sees the \"whole\" picture and embraces the team mentality when he comes on site to do a program. We planned a medium sized conference together earlier this year and he was always forthcoming with information and communicated changes/updates to me in a clear concise manner. When Dion came on site I felt like we were a team. He went out of his way to connect with the hotel staff which in turn made them truly want to go above and beyond to help him. I had a great time working with Dion and will recommend his services to any company looking to work with a meeting professional.",
    name: "Nancy Bakewell",
    role: "Empathetic L&D Professional Striving for Simplification through Data Analysis",
    initials: "NB",
    photo: "/testimonials/person-3.jpeg",
  },
  {
    quote:
      "Dion is one of the finest instructors that I have worked with. His presentations are dynamic and informational and he has always been an outstanding and caring mentor to his students.",
    name: "Ira Rosen, MA, CFEE",
    role: "Festival and Event Consultant",
    initials: "IR",
    photo: "/testimonials/person-7.jpeg",
  },
];

function StarRow() {
  return (
    <div className="flex items-center gap-1 text-[#FBBF24]" aria-label="5 star rating">
      {[0, 1, 2, 3, 4].map((i) => (
        <Star key={i} size={16} fill="currentColor" strokeWidth={0} />
      ))}
    </div>
  );
}

function TestimonialCard({ t }) {
  return (
    <article className="relative bg-white rounded-2xl border border-[var(--border)] p-6 md:p-7 w-[300px] sm:w-[340px] md:w-[380px] shrink-0 mx-3 md:mx-4 shadow-[0_10px_30px_-18px_rgba(11,37,69,0.25)] hover:shadow-[0_20px_50px_-18px_rgba(6,182,212,0.35)] hover:border-[var(--accent)]/30 transition-all duration-500 flex flex-col">
      <div className="flex items-start justify-between mb-4">
        <StarRow />
        <Quote size={22} className="text-[var(--accent)]/40" />
      </div>
      <p className="text-[15px] leading-relaxed text-[var(--text)]/80 italic flex-1">
        &ldquo;{t.quote}&rdquo;
      </p>
      <div className="mt-6 pt-5 border-t border-[var(--border)] flex items-center gap-4">
        {t.photo ? (
          <img
            src={t.photo}
            alt={t.name}
            loading="lazy"
            className="w-11 h-11 rounded-full object-cover border border-[var(--border)] shrink-0"
          />
        ) : t.logo ? (
          <div className="w-11 h-11 rounded-full bg-white border border-[var(--border)] flex items-center justify-center overflow-hidden shrink-0">
            <img src={LOGO_URL} alt="" className="w-full h-full object-contain p-1" />
          </div>
        ) : (
          <div className="w-11 h-11 rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 text-white flex items-center justify-center font-editorial text-sm font-medium shrink-0">
            {t.initials}
          </div>
        )}
        <div className="min-w-0">
          <div className="font-editorial text-[14px] leading-tight text-[var(--text)] tracking-tight">
            {t.name}
          </div>
          <div className="text-[11px] text-[var(--text-muted)] leading-snug mt-1 line-clamp-2">
            {t.role}
          </div>
        </div>
      </div>
      <span className="absolute left-0 top-6 bottom-6 w-[3px] rounded-r bg-gradient-to-b from-cyan-400 to-blue-600 opacity-70" />
    </article>
  );
}

export default function Testimonials() {
  return (
    <section
      id="testimonials"
      aria-label="Client testimonials"
      className="relative py-20 md:py-28 overflow-hidden bg-[var(--surface)]"
    >
      <div className="absolute inset-0 hex-pattern opacity-40 pointer-events-none" />
      <div className="brand-bloom brand-bloom-cyan w-[500px] h-[500px] -top-32 -left-24 opacity-30" />
      <div className="brand-bloom brand-bloom-blue w-[480px] h-[480px] -bottom-32 -right-20 opacity-30" />
      <HexMark
        size={220}
        opacity={0.05}
        className="absolute right-6 top-10 rotate-[18deg] pointer-events-none hidden lg:block"
      />

      <div className="relative max-w-[1600px] mx-auto px-4 sm:px-6 md:px-10 mb-10 md:mb-14">
        <Reveal>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div>
              <span className="chip">
                <Star size={12} fill="currentColor" strokeWidth={0} /> Testimonials
              </span>
              <h2 className="mt-5 md:mt-6 font-editorial text-3xl sm:text-4xl md:text-6xl leading-[1] tracking-[-0.03em] max-w-3xl">
                Trusted by planners,{" "}
                <span className="bg-gradient-to-r from-cyan-500 to-sky-600 bg-clip-text text-transparent italic font-medium">
                  praised by peers.
                </span>
              </h2>
            </div>
            <p className="max-w-sm text-[var(--text-muted)]">
              Kind words from directors, professors, and colleagues we&rsquo;ve
              had the privilege to work alongside.
            </p>
          </div>
        </Reveal>
      </div>

      <div className="relative">
        <Marquee gradient={false} pauseOnHover speed={28} className="py-4">
          {TESTIMONIALS.map((t, i) => (
            <TestimonialCard key={`row1-${i}`} t={t} />
          ))}
        </Marquee>
      </div>
    </section>
  );
}
